import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createMemoryRouter, RouterProvider } from "react-router-dom";
import { routes } from "./routes";
import {
  getRouteFromEntryPayload,
  scheduleBootNotifications,
} from "./lib/notifications";
import { useAuthStore } from "./stores/auth.store";
import { authenticateWithJest } from "./lib/jest-auth";
import { promptGuestLogin } from "./lib/jest-guest-login";
import { recoverIncompletePurchases } from "./services/payments/jest-payments";
import { initAnalytics, identifyUser } from "./lib/analytics";
// Side-effect import — Tailwind v4 + design tokens. Must come before render.
import "./globals.css";

// Boot sequence per FANT-14:
//   1. JestSDK.init({ autoLoginReminders: false }) — no-op in mock mode
//   2. Start the loading-progress heartbeat (must tick <15s; we tick every 5s)
//   3. Hydrate stores / fetch boot data (placeholder until FANT-19/FANT-20)
//   4. Render with an in-memory router (createMemoryRouter)
//   5. Stop the heartbeat
//
// Routing: createMemoryRouter, not createBrowserRouter. Jest serves the build
// from a CDN subdirectory, so window.location.pathname is never "/" — a
// URL-based router matches its "/" routes against that subpath and 404s.
// Memory routing holds route state in memory, independent of the hosting
// path. Deep-link state arrives via JestSDK.getEntryPayload(), not the URL.
//
// In mock mode (localhost / non-jest.com), JestSDK answers calls in-process,
// so this code path runs identically against the dev server.

// How often we re-push the latest progress value to Jest. Jest's host will
// terminate a stuck splash if it doesn't see a progress update within ~15s,
// so we tick well under that ceiling. The heartbeat continues even when a
// boot stage is slow (e.g. a long /api/v1/me round-trip) so the host keeps
// the splash alive.
const HEARTBEAT_INTERVAL_MS = 5000;

// Monotonic progress value (0..100). Stage transitions advance this; the
// heartbeat reads it on each tick. Module-scoped so the heartbeat closure
// always reports the latest value without a re-bind.
let progress = 0;

async function boot() {
  // Some dev environments may load main.tsx before the SDK script tag finishes.
  // Wait briefly for the global; fall through to a no-op stub if missing entirely
  // so the page still renders during early-stage dev.
  const sdk = await waitForJestSDK();

  // autoLoginReminders must stay false: when true, Jest renders a persistent
  // SMS opt-in / marketing-consent widget pinned to the bottom of the screen
  // that overlaps the BottomTabBar (fixed bottom-0) and blocks navigation. The
  // widget is host-rendered, so we can't reposition it from our CSS.
  if (sdk) await sdk.init({ autoLoginReminders: false });

  // No-ops unless VITE_PUBLIC_POSTHOG_KEY/_HOST are set. Init before auth so the
  // first identify() lands on an initialized client.
  initAnalytics();

  // Start the heartbeat immediately after init so any slow stage below is
  // covered by the <15s tick contract. It pushes the *current* `progress`
  // value on every tick — stage transitions update that value out-of-band.
  const stopHeartbeat = startProgressHeartbeat(sdk);

  try {
    reportProgress(sdk, 10); // SDK ready

    // Auto-login: re-derive a fresh FantasyPRIME session from Jest on every
    // load. Jest owns identity (auth is intentionally not persisted across
    // reloads), so without this the player would be signed out on each boot.
    // Both registered and guest players get a full account — guests are
    // authenticated with jestGuest=true so they can use the app without
    // friction (Jest compliance).
    if (sdk) {
      try {
        const auth = await authenticateWithJest(sdk);
        const store = useAuthStore.getState();
        store.setAuth(auth.accessToken, auth.sessionId);
        if (auth.user) {
          store.setUser(auth.user);
          // distinct_id = FantasyPRIME user.id (stable for guests too), so Jest
          // events stitch to the same person as web/server.
          identifyUser(auth.user);
        } else {
          store.setLoading(false);
        }
      } catch (err) {
        console.error("[jest-client] Auto-login failed:", err);
        useAuthStore.getState().setLoading(false);
      }
    } else {
      // Degraded mode (no SDK) — resolve loading so the guest UI renders.
      useAuthStore.getState().setLoading(false);
    }
    reportProgress(sdk, 20); // signed in

    // Fire-and-forget: notification scheduling makes its own network calls
    // (/api/v1/me etc.) and nothing downstream depends on it, so it must not
    // block time-to-first-render. Matches the login page's void-call pattern.
    // Runs after auto-login so those calls carry the freshly set token.
    void scheduleBootNotifications(sdk);

    // Drain any purchases that didn't finalize on a previous run (crash,
    // network drop between beginPurchase and completePurchase, etc.). The
    // Jest docs require this on every startup. Guests have no token and
    // therefore no incomplete purchases — skip silently.
    if (sdk) {
      const accessToken = useAuthStore.getState().accessToken;
      if (accessToken) {
        try {
          const recovered = await recoverIncompletePurchases(sdk, accessToken);
          if (recovered > 0) {
            console.info(
              `[jest-client] Recovered ${recovered} incomplete purchase(s)`,
            );
          }
        } catch (err) {
          // Don't block boot on recovery — Jest will surface the same
          // purchases next startup. Log so we notice persistent failures.
          console.error("[jest-client] Purchase recovery failed:", err);
        }
      }
    }
    reportProgress(sdk, 30); // recovery done

    // TODO (FANT-19/FANT-20): fetch /api/v1/me and /api/v1/events here.
    // Placeholder completes immediately. The entry payload (referralCode,
    // attribution, etc.) is available via sdk.getEntryPayload() — a future
    // step can seed initialEntries from it to deep-link into a contest.
    await Promise.resolve();
    reportProgress(sdk, 80); // boot data ready
    // TODO (FANT-19/FANT-20): hydrate stores from /api/v1/events if still needed
    const entryRoute = getRouteFromEntryPayload(sdk?.getEntryPayload() ?? null);
    const router = createMemoryRouter(routes, {
      initialEntries: entryRoute ? [entryRoute] : ["/"],
    });

    const rootEl = document.getElementById("root");
    if (!rootEl) throw new Error("No #root element");

    createRoot(rootEl).render(
      <StrictMode>
        <RouterProvider router={router} />
      </StrictMode>,
    );

    reportProgress(sdk, 100); // rendered

    // Guest nudge: once the app is mounted, give unregistered (guest) players
    // Jest's login/registration overlay. Fire-and-forget — boot completes
    // regardless of whether they sign in or dismiss it (guests keep full
    // access). On success it re-auths so the running UI reflects the new
    // registered state without a reload. See lib/jest-guest-login.
    if (sdk) void promptGuestLogin(sdk);
  } finally {
    stopHeartbeat();
  }
}

function reportProgress(sdk: JestSDKApi | null, value: number): void {
  const next = Math.max(progress, Math.min(100, Math.round(value)));
  progress = next;
  sdk?.setLoadingProgress(next);
}

// Heartbeat: re-pushes the current `progress` value on every tick so Jest's
// host keeps the splash alive across slow stages. Returns a stopper.
function startProgressHeartbeat(sdk: JestSDKApi | null): () => void {
  if (!sdk) return () => {};
  const id = setInterval(() => {
    sdk.setLoadingProgress(progress);
  }, HEARTBEAT_INTERVAL_MS);
  return () => clearInterval(id);
}

// Polls for the JestSDK global for up to 2s before giving up.
// In mock mode (localhost) the SDK script tag may not be reachable;
// the app should still load against the local Vite dev server.
async function waitForJestSDK(): Promise<JestSDKApi | null> {
  const deadline = Date.now() + 2000;
  while (Date.now() < deadline) {
    if (typeof window.JestSDK !== "undefined") return window.JestSDK;
    await new Promise((r) => setTimeout(r, 50));
  }
  console.warn(
    "[jest-client] JestSDK global not found; running in degraded mode",
  );
  return null;
}

boot().catch((err) => {
  console.error("[jest-client] Boot failed:", err);
});
